import * as THREE from 'three';

// Soglie snap
const WALL_SNAP_DISTANCE   = 0.2;  // 20 cm – parete→parete
const FURN_SNAP_ACQUIRE    = 0.2;  // 20 cm – mobile entra nello snap
const FURN_SNAP_RELEASE    = 0.35; // 35 cm – mobile esce dallo snap (isteresi)
const WALL_THICKNESS       = 0.2;  // spessore parete (deve combaciare con Wall.thickness)
const CORNER_EXTENSION     = WALL_THICKNESS / 2; // 10 cm – estensione angolo

class SelectionController {
  constructor(camera, domElement, furnitureController, controls) {
    this.camera = camera;
    this.domElement = domElement;
    this.furnitureController = furnitureController;
    this.controls = controls;

    this.raycaster   = new THREE.Raycaster();
    this.mouse       = new THREE.Vector2();
    this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

    // Selezione
    this.selectedWalls     = [];
    this.selectedFurniture = [];
    this.selectedDoors     = [];
    this.selectedWindows   = [];

    // Drag pareti
    this.isDraggingWall    = false;
    this.draggedWall       = null;
    this.wallDragStartPos  = null;
    this.wallOriginalStart = null;
    this.wallOriginalEnd   = null;
    this.wallDragMoved     = false;
    this._lastWallDragGP   = null;  // ultimo ground point durante il drag parete

    // Drag mobili
    this.isDragging          = false;
    this.dragStartPos        = null;
    this.dragOffset          = new THREE.Vector3();
    this._furnitureDragMoved = false;  // true se il drag ha prodotto movimento reale

    // Drag multiplo (ctrl + oggetti misti selezionati)
    this.isMultiDrag           = false;
    this._multiDragStartPos    = null;
    this._multiDragMoved       = false;
    this._multiDragFurnOffsets = [];   // [{f, origPos}]
    this._multiDragWallOffsets = [];   // [{w, origStart, origEnd}]

    // Hover
    this.hoveredWall      = null;
    this.hoveredFurniture = null;
    this.hoveredDoor      = null;
    this.hoveredWindow    = null;

    // Riferimenti controller
    this.wallController   = null;
    this.doorController   = null;
    this.windowController = null;

    // Stato snap
    this._snapMarkers     = [];   // array di mesh (max 2: una per parete)
    this._snapSceneAdded  = false;
    this._snapActive      = false;
    // Stato isteresi snap mobili
    this._furnitureSnapped   = false; // attualmente agganciato?
    this._snapWall           = null;  // parete agganciata
    this._snapWallNormal     = null;  // normale (verso il mobile)

    this._createSnapIndicator();

    // Stato lucchetto
    this.isLocked = false;

    // Timer debounce selezione porte/finestre (evita flash al doppio click)
    this._doorWindowSelectTimer = null;

    // Timer debounce selezione mobili (stesso schema di porte/finestre)
    this._furnitureSelectTimer = null;

    // Callback opzionale: viene chiamata ogni volta che la selezione cambia
    this.onSelectionChange = null;

    // Bind
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp   = this.onMouseUp.bind(this);
    this.onClick     = this.onClick.bind(this);
    this.onKeyDown   = this.onKeyDown.bind(this);

    // Touch
    this._lastTapTime    = 0;
    // Timestamp dell'ultimo touchend: usato per rilevare il doppio tap
    // e bloccare la selezione quando l'utente vuole solo fare toggle/audio
    this._lastTapEndTime = 0;
    // Flag attivo durante un drag touch: impedisce che onSelectionChange attivi
    // i bottoni (ruota/duplica/elimina) mentre l'utente sta ancora trascinando
    this._isTouchDrag = false;
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove  = this._onTouchMove.bind(this);
    this._onTouchEnd   = this._onTouchEnd.bind(this);

    this.setupEventListeners();
  }

  // SNAP – sfere visive (max 2, una per parete agganciata)
  _makeOneMarker() {
    const sGeo = new THREE.SphereGeometry(0.12, 16, 16);
    const sMat = new THREE.MeshBasicMaterial({
      color: 0xffcc00, transparent: true, opacity: 1, depthTest: false
    });
    const marker = new THREE.Mesh(sGeo, sMat);
    marker.renderOrder = 999;
    marker.visible = false;
    const rGeo = new THREE.RingGeometry(0.16, 0.26, 32);
    const rMat = new THREE.MeshBasicMaterial({
      color: 0xffcc00, transparent: true, opacity: 1,
      side: THREE.DoubleSide, depthTest: false
    });
    const ring = new THREE.Mesh(rGeo, rMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = -0.11;
    marker.add(ring);
    return marker;
  }

  _createSnapIndicator() {
    this._snapMarkers = [this._makeOneMarker(), this._makeOneMarker()];
  }

  _ensureSnapInScene() {
    if (this._snapSceneAdded) return;
    const scene = this.furnitureController?.scene;
    if (!scene) return;
    this._snapMarkers.forEach(m => scene.add(m));
    this._snapSceneAdded = true;
  }

  /**
   * Mostra i marker nei punti indicati (1 o 2 punti)
   * Gli eventuali marker in eccesso vengono nascosti
   */
  _showSnapMarkers(points) {
    this._ensureSnapInScene();
    this._snapMarkers.forEach((m, i) => {
      if (i < points.length) {
        m.position.set(points[i].x, 0.12, points[i].z);
        m.visible = true;
      } else {
        m.visible = false;
      }
    });
    this._snapActive = true;
    window.requestRender?.();
  }

  _hideSnapMarkers() {
    this._snapMarkers.forEach(m => { if (m) m.visible = false; });
    this._snapActive = false;
    window.requestRender?.();
  }

  // GEOMETRIA SNAP
  // Punto più vicino su un segmento (2D, Y ignorato)
  _closestPointOnSeg(p, a, b) {
    const px = p.x, pz = p.z;
    const ax = a.x, az = a.z;
    const dx = b.x - ax, dz = b.z - az;
    const len2 = dx * dx + dz * dz;
    let t = 0;
    if (len2 > 0) t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2));
    const cx = ax + t * dx;
    const cz = az + t * dz;
    return {
      point: new THREE.Vector3(cx, 0, cz),
      dist:  Math.sqrt((px - cx) ** 2 + (pz - cz) ** 2),
      t      // parametro normalizzato (0=start, 1=end)
    };
  }

  /**
   * Trova il punto più vicino su qualsiasi altra parete entro WALL_SNAP_DISTANCE
   * per l'endpoint della parete in drag
   *
   * Ritorna { snapPoint, isBodySnap } dove:
   *   isBodySnap = true  → il punto è sul corpo della parete (non su un suo estremo)
   *   isBodySnap = false → il punto è su un endpoint di un'altra parete
   */
  _findWallSnapForEndpoint(endpoint, excludeWall) {
    if (!this.wallController) return null;
    let best = null;
    let bestDist = WALL_SNAP_DISTANCE;

    for (const w of this.wallController.getAllWalls()) {
      if (w === excludeWall) continue;

      const cp = this._closestPointOnSeg(endpoint, w.start, w.end);
      if (cp.dist < bestDist) {
        bestDist = cp.dist;

        // Determina se il punto è su un endpoint o sul corpo
        const epTolerance = 0.02; // 2 cm per considerarlo un endpoint
        const isBodySnap = cp.t > epTolerance && cp.t < (1 - epTolerance);

        best = { snapPoint: cp.point.clone(), isBodySnap };
      }
    }
    return best;
  }

  /**
   * Proiezione OBB: semi-estensione del mobile nella direzione di una normale
   *
   * Tiene conto della rotazione del mobile:
   *   halfExtent = |dot(n, fwd)| * depth/2  +  |dot(n, right)| * width/2
   *
   * Con furniture.rotationY in radianti (asse Y, senso orario dall'alto):
   *   fwd   = (sin(r), 0, cos(r))   – asse profondità del mobile
   *   right = (cos(r), 0, -sin(r))  – asse larghezza del mobile
   *
   * @param {THREE.Vector3} normal   – normale unitaria (direzione di test)
   * @param {number}        rotY     – rotazione mobile in radianti
   * @param {number}        width    – larghezza mobile (X locale)
   * @param {number}        depth    – profondità mobile (Z locale)
   * @returns {number} semi-estensione in metri
   */
  _obbHalfExtent(normal, rotY, width, depth) {
    const fwdX  =  Math.sin(rotY);
    const fwdZ  =  Math.cos(rotY);
    const rgtX  =  Math.cos(rotY);
    const rgtZ  = -Math.sin(rotY);
    return Math.abs(normal.x * fwdX + normal.z * fwdZ) * (depth  / 2)
         + Math.abs(normal.x * rgtX + normal.z * rgtZ) * (width  / 2);
  }

  /**
   * Raccoglie tutte le pareti entro la soglia di snap, ordinate per surfaceDist crescente
   * Usato da _resolveWallSnap per gestire snap singolo e multi-parete
   *
   * @param {THREE.Vector3} pos    – centro naturale del mobile
   * @param {number}        rotY   – rotazione mobile in radianti
   * @param {number}        width  – larghezza mobile
   * @param {number}        depth  – profondità mobile
   * @param {number}        radius – soglia distanza superficie
   * @returns {Array} candidati ordinati per surfaceDist crescente
   */
  _collectWallSnapCandidates(pos, rotY, width, depth, radius) {
    if (!this.wallController) return [];
    const candidates = [];

    for (const w of this.wallController.getAllWalls()) {
      const cp = this._closestPointOnSeg(pos, w.start, w.end);

      const toObj = new THREE.Vector3(pos.x - cp.point.x, 0, pos.z - cp.point.z);
      const wallAngle = w.getAngle();
      const nx0 = -Math.sin(wallAngle);
      const nz0 =  Math.cos(wallAngle);
      const normal = new THREE.Vector3(nx0, 0, nz0);
      if (toObj.dot(normal) < 0) normal.negate();

      const halfExtent  = this._obbHalfExtent(normal, rotY, width, depth);
      const wallHalf    = (w.thickness ?? WALL_THICKNESS) / 2;
      const surfaceDist = cp.dist - halfExtent - wallHalf;

      if (surfaceDist < radius) {
        candidates.push({ point: cp.point.clone(), wall: w, normal: normal.clone(),
                          halfExtent, wallHalf, surfaceDist });
      }
    }
    candidates.sort((a, b) => a.surfaceDist - b.surfaceDist);
    return candidates;
  }

  /**
   * Risolve la posizione di snap dato un set di candidati
   *
   * 1 parete  → proiezione del basePos lungo la parete (scorrimento fluido)
   * 2+ pareti → intersezione delle linee di contatto offset (snap ad angolo)
   *
   * Per l'intersezione 2D: per ogni parete i esiste il vincolo n_i · P = k_i
   * dove k_i = (punto_parete_i · n_i) + halfExtent_i + wallHalf_i
   * Si risolve il sistema 2×2 con la regola di Cramer
   * Se le pareti sono quasi parallele (|det| < 0.05) si usa solo la più vicina
   *
   * @param {THREE.Vector3} basePos    – centro naturale del mobile
   * @param {Array}         candidates – output di _collectWallSnapCandidates
   * @returns {{ snappedPos, primarySnap, multiSnap }} oppure null
   */
  _resolveWallSnap(basePos, candidates) {
    if (!candidates.length) return null;

    const a = candidates[0];

    if (candidates.length === 1) {
      // Snap singolo: scorrimento lungo la parete
      const ws  = new THREE.Vector3(a.wall.start.x, 0, a.wall.start.z);
      const we  = new THREE.Vector3(a.wall.end.x,   0, a.wall.end.z);
      const wd  = new THREE.Vector3().subVectors(we, ws).normalize();
      const wl  = ws.distanceTo(we);
      const bf  = new THREE.Vector3(basePos.x, 0, basePos.z);
      const t   = Math.max(0, Math.min(wl, new THREE.Vector3().subVectors(bf, ws).dot(wd)));
      const contactOffset = a.halfExtent + a.wallHalf;
      const snappedPos = ws.clone().add(wd.clone().multiplyScalar(t))
                           .add(a.normal.clone().multiplyScalar(contactOffset));
      snappedPos.y = 0;
      return { snappedPos, primarySnap: a, multiSnap: false };
    }

    // Snap multi-parete: intersezione dei vincoli planari (regola di Cramer)
    const b   = candidates[1];
    const ka  = a.point.dot(a.normal) + a.halfExtent + a.wallHalf;
    const kb  = b.point.dot(b.normal) + b.halfExtent + b.wallHalf;
    const det = a.normal.x * b.normal.z - a.normal.z * b.normal.x;

    if (Math.abs(det) < 0.05) {
      // Pareti quasi parallele: usa solo la più vicina con scorrimento
      return this._resolveWallSnap(basePos, [a]);
    }

    const px = (ka * b.normal.z - kb * a.normal.z) / det;
    const pz = (a.normal.x * kb - b.normal.x * ka) / det;
    const snappedPos = new THREE.Vector3(px, 0, pz);
    return { snappedPos, primarySnap: a, secondarySnap: b, multiSnap: true };
  }

  // SETUP
  setWallController(wc)   { this.wallController   = wc; }
  setDoorController(dc)   { this.doorController   = dc; }
  setWindowController(wc) { this.windowController = wc; }

  setLocked(locked) {
    this.isLocked = locked;
    if (locked) {
      // Annulla timer pendenti di selezione
      this.cancelDoorWindowSelect();
      this.cancelFurnitureSelect();

      // Interrompe qualsiasi drag in corso
      if (this.isDragging) {
        this._hideSnapMarkers();
        this._furnitureSnapped   = false;
        this._furnitureDragMoved = false;
        this.isDragging          = false;
        this.dragStartPos        = null;
        if (this.controls) this.controls.enabled = true;
      }
      if (this.isDraggingWall) {
        this._hideSnapMarkers();
        this.isDraggingWall  = false;
        this.draggedWall     = null;
        this.wallDragMoved   = false;
        this._lastWallDragGP = null;
        if (this.controls) this.controls.enabled = true;
      }
      if (this.isMultiDrag) {
        this.isMultiDrag           = false;
        this._multiDragFurnOffsets = [];
        this._multiDragWallOffsets = [];
        this._multiDragStartPos    = null;
        if (this.controls) this.controls.enabled = true;
      }

      // Deseleziona tutto e rimuove hover highlight quando si blocca
      this.deselectAll();
      [this.hoveredFurniture, this.hoveredDoor, this.hoveredWindow].forEach(obj => {
        if (obj) obj.mesh?.traverse(c => {
          if (c.isMesh && c.material) {
            const mats = Array.isArray(c.material) ? c.material : [c.material];
            mats.forEach(m => { if (m.emissive) m.emissive.setHex(0x000000); });
          }
        });
      });
      if (this.hoveredWall) {
        this.hoveredWall.mesh?.traverse(c => {
          if (c.isMesh && c.material?.emissive) c.material.emissive.setHex(0x000000);
        });
      }
      this.hoveredFurniture = null;
      this.hoveredDoor      = null;
      this.hoveredWindow    = null;
      this.hoveredWall      = null;
      this.onSelectionChange?.();
    }
  }

  setupEventListeners() {
    this.domElement.addEventListener('mousedown', this.onMouseDown);
    this.domElement.addEventListener('mousemove', this.onMouseMove);
    this.domElement.addEventListener('mouseup',   this.onMouseUp);
    this.domElement.addEventListener('click',     this.onClick);
    document.addEventListener('keydown', this.onKeyDown);

    // Listener per il touch
    this.domElement.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this.domElement.addEventListener('touchmove',  this._onTouchMove,  { passive: false });
    this.domElement.addEventListener('touchend',   this._onTouchEnd,   { passive: false });
  }

  // EVENT HANDLERS
  onMouseDown(event) {
    if (this.isLocked) return;
    if (event.button !== 0) return;
    const wall  = this.getIntersectedWall(event);
    const door  = this.getIntersectedDoor(event);
    const win   = this.getIntersectedWindow(event);
    const furn  = this.getIntersectedFurniture(event);
    const ctrl  = event.ctrlKey || event.metaKey;

    // Se il drawing mode è attivo e l'utente clicca su un oggetto esistente,
    // disattiva automaticamente il disegno pareti prima di procedere
    if (this.wallController?.drawingMode && (wall || door || win || furn)) {
      this.wallController.disableDrawMode();
      if (window.app) window.app.updateAllButtons();
    }

    if (furn) {
      if (ctrl) {
        // Aggiunge alla selezione se non c'è già, poi avvia subito il multi-drag
        if (!furn.isSelected) { furn.select(); this.selectedFurniture.push(furn); }
        this.onSelectionChange?.();
        this.startMultiDrag(event);
      } else {
        // Cancella il timer del click precedente: se questo mousedown è il secondo
        // di un doppio click, impedisce che TIMER A attivi i bottoni prima del dblclick
        this.cancelFurnitureSelect();
        this.startDrag(event, furn);
        return; // non chiamare onSelectionChange subito: ci pensa endDrag via timer
      }
    } else if (wall) {
      if (ctrl) {
        // Aggiunge alla selezione se non c'è già, poi avvia subito il multi-drag
        if (!this.selectedWalls.includes(wall)) { this.selectWall(wall); this.selectedWalls.push(wall); }
        this.onSelectionChange?.();
        this.startMultiDrag(event);
      } else {
        this.startWallDrag(event, wall);
      }
    } else if (door) {
      if (ctrl) { this.toggleDoorSelection(door); }
      else {
        // Debounce: aspetta 250ms prima di selezionare,
        // così il doppio click può cancellare prima che avvenga
        clearTimeout(this._doorWindowSelectTimer);
        this._doorWindowSelectTimer = setTimeout(() => {
          this.selectSingleDoor(door);
          this.onSelectionChange?.();
        }, 250);
        return;
      }
    } else if (win) {
      if (ctrl) { this.toggleWindowSelection(win); }
      else {
        clearTimeout(this._doorWindowSelectTimer);
        this._doorWindowSelectTimer = setTimeout(() => {
          this.selectSingleWindow(win);
          this.onSelectionChange?.();
        }, 250);
        return;
      }
    }
    this.onSelectionChange?.();
  }

  onMouseMove(event) {
    if (this.isLocked) return;
    if (this.isMultiDrag) {
      this.updateMultiDrag(event);
    } else if (this.isDragging && this.selectedFurniture.length > 0) {
      this.updateDrag(event);
    } else if (this.isDraggingWall) {
      this.updateWallDrag(event);
    } else {
      this.highlightHover(event);
    }
  }

  onMouseUp(event) {
    if (this.isMultiDrag)         this.endMultiDrag(event);
    else if (this.isDragging)     this.endDrag(event);
    else if (this.isDraggingWall) this.endWallDrag(event);
  }

  onClick(event) {
    if (this.isLocked) return;
    if (this.isDragging) return;
    if (this.wallDragMoved) { this.wallDragMoved = false; return; }

    const wall = this.getIntersectedWall(event);
    const door = this.getIntersectedDoor(event);
    const win  = this.getIntersectedWindow(event);
    const furn = this.getIntersectedFurniture(event);
    const ctrl = event.ctrlKey || event.metaKey;

    if (!wall && !door && !win && !furn && !ctrl) this.deselectAll();
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.deselectAll();
  }

  // SELEZIONE
  selectSingleWall(wall) {
    this.deselectAll(); this.selectWall(wall); this.selectedWalls = [wall];
  }
  toggleWallSelection(wall) {
    const i = this.selectedWalls.indexOf(wall);
    if (i === -1) { this.selectWall(wall); this.selectedWalls.push(wall); }
    else          { this.deselectWall(wall); this.selectedWalls.splice(i, 1); }
  }
  selectWall(wall) {
    if (wall.mesh) wall.mesh.traverse(c => {
      if (c.isMesh && c.material) c.material.emissive?.setHex(0xddeeff);
    });
    window.requestRender?.();
  }
  deselectWall(wall) {
    if (wall.mesh) wall.mesh.traverse(c => {
      if (c.isMesh && c.material) c.material.emissive?.setHex(0x000000);
    });
    window.requestRender?.();
  }

  selectSingleDoor(door) { this.deselectAll(); door.select(); this.selectedDoors = [door]; }
  toggleDoorSelection(door) {
    const i = this.selectedDoors.indexOf(door);
    if (i === -1) { door.select(); this.selectedDoors.push(door); }
    else          { door.deselect(); this.selectedDoors.splice(i, 1); }
  }

  selectSingleWindow(win) { this.deselectAll(); win.select(); this.selectedWindows = [win]; }
  toggleWindowSelection(win) {
    const i = this.selectedWindows.indexOf(win);
    if (i === -1) { win.select(); this.selectedWindows.push(win); }
    else          { win.deselect(); this.selectedWindows.splice(i, 1); }
  }

  selectSingleFurniture(f) {
    this.deselectAll(); f.select(); this.selectedFurniture = [f];
    console.log('Selezionato:', f.catalogItem.name);
  }
  toggleFurnitureSelection(f) {
    const i = this.selectedFurniture.indexOf(f);
    if (i === -1) { f.select(); this.selectedFurniture.push(f); }
    else          { f.deselect(); this.selectedFurniture.splice(i, 1); }
  }

  deselectAll() {
    this.selectedWalls.forEach(w => this.deselectWall(w));     this.selectedWalls     = [];
    this.selectedDoors.forEach(d => d.deselect());             this.selectedDoors     = [];
    this.selectedWindows.forEach(w => w.deselect());           this.selectedWindows   = [];
    this.selectedFurniture.forEach(f => f.deselect());         this.selectedFurniture = [];
    this.onSelectionChange?.();
    window.requestRender?.();
  }

  // OPERAZIONI
  rotateSelected(degrees = 45) {
    if (this.isLocked) return;
    let count = 0;
    if (this.wallController) {
      this.selectedWalls.forEach(w => { this.wallController.rotateWall(w.id, 90); count++; });

      // Rebase drag parete singola post-rotazione
      if (this.isDraggingWall && this.draggedWall && this._lastWallDragGP) {
        this.wallOriginalStart = this.draggedWall.start.clone();
        this.wallOriginalEnd   = this.draggedWall.end.clone();
        this.wallDragStartPos  = this._lastWallDragGP.clone();
      }
    }
    this.selectedFurniture.forEach(f => {
      if (window.app?.undoRedoManager)
        window.app.executeCommand(new window.RotateFurnitureCommand(this.furnitureController, f.id, degrees));
      else
        this.furnitureController.rotateFurniture(f.id, degrees);
      count++;
    });
    this.selectedDoors.forEach(d => {
      if (window.app?.undoRedoManager) window.app.executeCommand(new window.FlipOpeningCommand(d));
      else d.flip();
      count++;
    });
    this.selectedWindows.forEach(w => {
      if (window.app?.undoRedoManager) window.app.executeCommand(new window.FlipOpeningCommand(w));
      else w.flip();
      count++;
    });

    // Rebase multi-drag post-rotazione: aggiorna le posizioni originali di tutti
    // gli oggetti selezionati così il prossimo mousemove parte dalle posizioni ruotate
    if (this.isMultiDrag && this._multiDragStartPos) {
      const lastGP = this._multiDragStartPos.clone().add(
        // ricava l'ultimo delta applicato dal confronto posizione attuale vs originale
        this._multiDragFurnOffsets.length > 0
          ? new THREE.Vector3().subVectors(
              this._multiDragFurnOffsets[0].f.position,
              this._multiDragFurnOffsets[0].origPos
            )
          : this._multiDragWallOffsets.length > 0
            ? new THREE.Vector3().subVectors(
                this._multiDragWallOffsets[0].w.start,
                this._multiDragWallOffsets[0].origStart
              )
            : new THREE.Vector3()
      );
      this._multiDragFurnOffsets = this.selectedFurniture.map(f => ({
        f, origPos: f.position.clone()
      }));
      this._multiDragWallOffsets = this.selectedWalls.map(w => ({
        w, origStart: w.start.clone(), origEnd: w.end.clone()
      }));
      this._multiDragStartPos = lastGP.clone();
    }

    if (count > 0) {
      console.log(`Ruotati ${count} oggetti`);
      window.requestRender?.();
    }
  }

  deleteSelected() {
    if (this.isLocked) return;
    let count = 0;
    if (this.wallController) {
      this.selectedWalls.forEach(w => {
        if (window.app?.undoRedoManager) window.app.executeCommand(new window.RemoveWallCommand(this.wallController, w.id));
        else this.wallController.removeWall(w.id);
        count++;
      });
      this.selectedWalls = [];
    }
    if (this.doorController) {
      this.selectedDoors.forEach(d => {
        if (window.app?.undoRedoManager) window.app.executeCommand(new window.RemoveDoorCommand(this.doorController, d.id));
        else this.doorController.removeDoor(d.id);
        count++;
      });
      this.selectedDoors = [];
    }
    if (this.windowController) {
      this.selectedWindows.forEach(w => {
        if (window.app?.undoRedoManager) window.app.executeCommand(new window.RemoveWindowCommand(this.windowController, w.id));
        else this.windowController.removeWindow(w.id);
        count++;
      });
      this.selectedWindows = [];
    }
    this.selectedFurniture.forEach(f => {
      if (window.app?.undoRedoManager) window.app.executeCommand(new window.RemoveFurnitureCommand(this.furnitureController, f.id));
      else this.furnitureController.removeFurniture(f.id);
      count++;
    });
    this.selectedFurniture = [];
    if (count > 0) {
      console.log(`Eliminati ${count} oggetti`);
      window.requestRender?.();
    }
    this.onSelectionChange?.();
  }

  duplicateSelected() {
    if (this.isLocked) return;
    let count = 0;
    if (this.wallController) {
      const originalWalls = [...this.selectedWalls];
      originalWalls.forEach(w => {
        const nw = this.wallController.duplicateWall(w.id);
        if (nw) count++;
      });
      // Mantiene selezionate le pareti originali → duplicazioni successive
      // continuano a partire dall'originale e non dall'ultima copia
      if (count > 0) this.onSelectionChange?.();
    }
    this.selectedFurniture.forEach(f => { this.furnitureController.duplicateFurniture(f.id); count++; });
    if (count > 0) {
      console.log(`Duplicati ${count} oggetti`);
      window.requestRender?.();
    }
  }

  // DRAG MULTIPLO – sposta insieme furniture e pareti selezionati (ctrl+drag)
  startMultiDrag(event) {
    const gp = this.getGroundIntersection(event);
    if (!gp) return;
    this.isMultiDrag        = true;
    this._multiDragMoved    = false;
    this._multiDragStartPos = gp.clone();
    // Salva posizioni originali di tutti gli oggetti selezionati
    this._multiDragFurnOffsets = this.selectedFurniture.map(f => ({
      f, origPos: f.position.clone()
    }));
    this._multiDragWallOffsets = this.selectedWalls.map(w => ({
      w,
      origStart: w.canonicalStart.clone(),
      origEnd:   w.canonicalEnd.clone()
    }));
    if (this.controls) this.controls.enabled = false;
  }

  updateMultiDrag(event) {
    if (!this.isMultiDrag) return;
    const gp = this.getGroundIntersection(event);
    if (!gp) return;

    const delta = new THREE.Vector3().subVectors(gp, this._multiDragStartPos);
    delta.y = 0;

    if (!this._multiDragMoved && delta.length() > 0.02) {
      this._multiDragMoved = true;
    }

    // Muovi tutti i mobili
    this._multiDragFurnOffsets.forEach(({ f, origPos }) => {
      const newPos = origPos.clone().add(delta);
      this.furnitureController.moveFurniture(f.id, newPos);
    });

    // Muovi tutte le pareti
    this._multiDragWallOffsets.forEach(({ w, origStart, origEnd }) => {
      const newStart = origStart.clone().add(delta);
      const newEnd   = origEnd.clone().add(delta);
      // Aggiorna le posizioni canoniche (nessuno snap nel drag multiplo)
      w.canonicalStart.copy(newStart);
      w.canonicalEnd.copy(newEnd);
      if (this.wallController) this.wallController.moveWall(w.id, newStart, newEnd);
    });
    window.requestRender?.();
  }

  endMultiDrag(event) {
    this.isMultiDrag        = false;
    this._multiDragStartPos = null;
    this._multiDragFurnOffsets = [];
    this._multiDragWallOffsets = [];
    if (this.controls) this.controls.enabled = true;

    if (this._multiDragMoved) {
      this.deselectAll();
    }
    // Se non c'è stato movimento rimane la selezione invariata
    this._multiDragMoved = false;
  }

  // DRAG PARETI
  // Snap lungo tutta la lunghezza + corner trick agli incroci
  // applyHighlight=false su touch: stessa logica di startDrag per evitare flash sul doppio tap.
  startWallDrag(event, wall, applyHighlight = true) {
    const gp = this.getGroundIntersection(event);
    if (!gp) return;
    // Seleziona visivamente e notifica subito → bottoni attivi durante tutto il drag
    this.deselectAll();
    if (applyHighlight) this.selectWall(wall);
    this.selectedWalls     = [wall];
    if (applyHighlight) this.onSelectionChange?.();
    this.isDraggingWall    = true;
    this.draggedWall       = wall;
    this.wallDragStartPos  = gp.clone();
    this.wallOriginalStart = wall.canonicalStart.clone();
    this.wallOriginalEnd   = wall.canonicalEnd.clone();
    this.wallDragMoved     = false;
    if (this.controls) this.controls.enabled = false;
  }

  updateWallDrag(event) {
    if (!this.isDraggingWall || !this.draggedWall) return;
    const gp = this.getGroundIntersection(event);
    if (!gp) return;

    this._lastWallDragGP = gp.clone();

    const delta = new THREE.Vector3().subVectors(gp, this.wallDragStartPos);
    delta.y = 0;
    if (!this.wallDragMoved && delta.length() > 0.05) {
      this.wallDragMoved = true;
      // Se l'highlight era stato rimandato (touch), applicalo ora che è un vero drag
      const draggedW = this.draggedWall;
      if (draggedW && !this.selectedWalls.includes(draggedW)) {
        this.selectWall(draggedW);
        this.selectedWalls = [draggedW];
      } else if (draggedW) {
        this.selectWall(draggedW);
      }
      // Su touch non notificare: i bottoni non devono attivarsi durante il trascinamento
      if (!this._isTouchDrag) this.onSelectionChange?.();
    }

    let newStart = this.wallOriginalStart.clone().add(delta);
    let newEnd   = this.wallOriginalEnd.clone().add(delta);

    // Direzione e lunghezza originali — servono per preservare la geometria
    // della parete quando un solo endpoint fa snap
    const originalDir = new THREE.Vector3()
      .subVectors(this.wallOriginalEnd, this.wallOriginalStart)
      .normalize();
    const originalLength = this.wallOriginalStart.distanceTo(this.wallOriginalEnd);

    const snapS = this._findWallSnapForEndpoint(newStart, this.draggedWall);
    const snapE = this._findWallSnapForEndpoint(newEnd,   this.draggedWall);

    if (snapS && snapE) {
      // Entrambi gli endpoint snappano → giunzione angolo
      const snapPointS = snapS.snapPoint.clone();
      const snapPointE = snapE.snapPoint.clone();
      const wallDir = new THREE.Vector3().subVectors(snapPointE, snapPointS).normalize();
      newStart = snapPointS.clone().sub(wallDir.clone().multiplyScalar(CORNER_EXTENSION));
      newEnd   = snapPointE.clone().add(wallDir.clone().multiplyScalar(CORNER_EXTENSION));
      // Posizioni canoniche = punti di snap senza estensione
      this.draggedWall.canonicalStart.copy(snapPointS);
      this.draggedWall.canonicalEnd.copy(snapPointE);

    } else if (snapS) {
      // Solo l'estremo iniziale snappa: ancora lì, ricostruisce l'altro
      // mantenendo direzione e lunghezza canoniche → la parete non si inclina
      const snapPoint  = snapS.snapPoint.clone();
      const canonEnd   = snapPoint.clone().add(originalDir.clone().multiplyScalar(originalLength));
      newStart = snapPoint.clone().sub(originalDir.clone().multiplyScalar(CORNER_EXTENSION));
      newEnd   = canonEnd;
      this.draggedWall.canonicalStart.copy(snapPoint);
      this.draggedWall.canonicalEnd.copy(canonEnd);

    } else if (snapE) {
      // Solo l'estremo finale snappa: ancora lì, ricostruisce l'altro
      const snapPoint  = snapE.snapPoint.clone();
      const canonStart = snapPoint.clone().sub(originalDir.clone().multiplyScalar(originalLength));
      newEnd   = snapPoint.clone().add(originalDir.clone().multiplyScalar(CORNER_EXTENSION));
      newStart = canonStart;
      this.draggedWall.canonicalEnd.copy(snapPoint);
      this.draggedWall.canonicalStart.copy(canonStart);

    } else {
      // Nessuno snap: posizioni attuali = canoniche (nessuna estensione)
      this.draggedWall.canonicalStart.copy(newStart);
      this.draggedWall.canonicalEnd.copy(newEnd);
    }

    // Feedback visivo: un marker per ogni endpoint snappato
    const markerPts = [];
    if (snapS) markerPts.push(snapS.snapPoint);
    if (snapE) markerPts.push(snapE.snapPoint);

    if (markerPts.length) {
      this._showSnapMarkers(markerPts);
    } else {
      this._hideSnapMarkers();
    }

    if (this.wallController) {
      this.wallController.moveWall(this.draggedWall.id, newStart, newEnd);
    }
    window.requestRender?.();
  }

  endWallDrag(event) {
    const moved = this.wallDragMoved;
    this._hideSnapMarkers();
    this.isDraggingWall    = false;
    this.draggedWall       = null;
    this.wallDragStartPos  = null;
    this.wallOriginalStart = null;
    this.wallOriginalEnd   = null;
    this._lastWallDragGP   = null;
    if (this.controls) this.controls.enabled = true;

    if (moved) {
      // Era un vero drag: deseleziona → bottoni si disabilitano
      this.deselectAll();
    } else {
      // Era un semplice tap: stesso schema debounce di porte/finestre
      // Cattura la parete nel closure così il timer è indipendente da this.selectedWalls
      const pendingWall = this.selectedWalls[0] ?? null;
      clearTimeout(this._doorWindowSelectTimer);
      this._doorWindowSelectTimer = setTimeout(() => {
        if (pendingWall) {
          this.selectSingleWall(pendingWall);
          this.onSelectionChange?.();
          window.requestRender?.();
        }
      }, 250);
    }
    if (moved) console.log('Drag parete completato');
  }

  // DRAG MOBILI – snap a parete (solo posizione, no rotazione)
  // applyHighlight=false su touch: l'highlight viene applicato solo se il drag
  // produce movimento reale, così il doppio tap non causa alcun flash visivo
  startDrag(event, furniture, applyHighlight = true) {
    if (!furniture.isSelected) {
      this.deselectAll();
      if (applyHighlight) furniture.select();
      this.selectedFurniture = [furniture];
      // onSelectionChange NON viene chiamato qui → i bottoni non si attivano
    }
    const gp = this.getGroundIntersection(event);
    if (!gp) return;
    this.isDragging          = true;
    this._furnitureDragMoved = false;
    this.dragStartPos        = gp.clone();
    this._furnitureSnapped   = false;
    this._snapWall           = null;
    this._snapWallNormal     = null;
    if (this.controls) this.controls.enabled = false;
    this.dragOffset = new THREE.Vector3().subVectors(furniture.position, gp);
  }

  updateDrag(event) {
    const gp = this.getGroundIntersection(event);
    if (!gp) return;

    // basePos: posizione "naturale" del mobile (senza snap)
    const basePos = gp.clone().add(this.dragOffset);

    // Rileva movimento significativo (> 2cm)
    if (!this._furnitureDragMoved && this.dragStartPos &&
        gp.distanceTo(this.dragStartPos) > 0.02) {
      this._furnitureDragMoved = true;
      // Se l'highlight era stato rimandato (touch), applicalo ora che è un vero drag
      const f0ref = this.selectedFurniture[0];
      if (f0ref && !f0ref.isSelected) {
        f0ref.select();
        // Su touch non notificare: i bottoni non devono attivarsi durante il trascinamento
        if (!this._isTouchDrag) this.onSelectionChange?.();
      }
    }

    const f0    = this.selectedFurniture[0];
    const dims  = f0.catalogItem.dimensions;
    // Rotazione del mobile in radianti (THREE usa Y negativo per senso orario)
    const rotY  = -(f0.mesh?.rotation?.y ?? 0);

    // Rilevazione snap OBB multi-parete:
    //   1 parete  → scorrimento fluido lungo la parete
    //   2+ pareti → intersezione dei vincoli planari (snap ad angolo)
    const detectRadius = this._furnitureSnapped ? FURN_SNAP_RELEASE : FURN_SNAP_ACQUIRE;
    const candidates   = this._collectWallSnapCandidates(
      basePos, rotY, dims.width, dims.depth, detectRadius
    );
    const resolved = this._resolveWallSnap(basePos, candidates);

    if (resolved) {
      // Snap attivo
      this._furnitureSnapped = true;
      this._snapWall         = resolved.primarySnap.wall;
      this._snapWallNormal   = resolved.primarySnap.normal;

      const snappedBase = resolved.snappedPos.clone();

      // Marker: un cerchio per ogni parete agganciata, sul punto di scorrimento
      const markerPoints = candidates.slice(0, 2).map(snap => {
        const ws = new THREE.Vector3(snap.wall.start.x, 0, snap.wall.start.z);
        const we = new THREE.Vector3(snap.wall.end.x,   0, snap.wall.end.z);
        const wd = new THREE.Vector3().subVectors(we, ws).normalize();
        const wl = ws.distanceTo(we);
        const bf = new THREE.Vector3(basePos.x, 0, basePos.z);
        const t  = Math.max(0, Math.min(wl, new THREE.Vector3().subVectors(bf, ws).dot(wd)));
        return ws.clone().add(wd.clone().multiplyScalar(t));
      });
      this._showSnapMarkers(markerPoints);

      this.selectedFurniture.forEach((f, idx) => {
        const relOffset = idx === 0
          ? new THREE.Vector3()
          : new THREE.Vector3().subVectors(f.position, f0.position);
        this.furnitureController.moveFurniture(f.id, snappedBase.clone().add(relOffset), false);
      });

    } else {
      // Movimento libero
      this._furnitureSnapped = false;
      this._snapWall         = null;
      this._snapWallNormal   = null;
      this._hideSnapMarkers();

      this.selectedFurniture.forEach(f => {
        const relOffset = new THREE.Vector3().subVectors(
          f.position, this.selectedFurniture[0].position
        );
        this.furnitureController.moveFurniture(f.id, basePos.clone().add(relOffset));
      });
    }
    window.requestRender?.();
  }

  endDrag(event) {
    this._hideSnapMarkers();
    this._furnitureSnapped = false;
    this._snapWall         = null;
    this._snapWallNormal   = null;
    this.isDragging        = false;
    this.dragStartPos      = null;
    if (this.controls) this.controls.enabled = true;

    if (this._furnitureDragMoved) {
      // Era un vero drag: deseleziona senza attivare i bottoni
      this._furnitureDragMoved = false;
      this.deselectAll();
      console.log('Drag mobile completato');
    } else {
      // Era un semplice tap: stesso schema debounce di porte/finestre
      // Cattura il mobile nel closure così il timer è indipendente da this.selectedFurniture
      const pendingFurn = this.selectedFurniture[0] ?? null;
      clearTimeout(this._furnitureSelectTimer);
      this._furnitureSelectTimer = setTimeout(() => {
        if (pendingFurn) {
          this.selectSingleFurniture(pendingFurn);
          this.onSelectionChange?.();
          window.requestRender?.();
        }
      }, 250);
    }
  }

  // HOVER HIGHLIGHT
  highlightHover(event) {
    const wall = this.getIntersectedWall(event);
    const door = this.getIntersectedDoor(event);
    const win  = this.getIntersectedWindow(event);
    const furn = this.getIntersectedFurniture(event);

    if (this.hoveredWall && this.hoveredWall !== wall) {
      if (!this.selectedWalls.includes(this.hoveredWall)) {
        this.hoveredWall.mesh?.traverse(c => {
          if (c.isMesh && c.material?.emissive) c.material.emissive.setHex(0x000000);
        });
      }
      this.hoveredWall = null;
    }
    if (this.hoveredDoor && this.hoveredDoor !== door) {
      if (!this.hoveredDoor.isSelected) {
        this.hoveredDoor.mesh?.traverse(c => {
          if (c.material?.emissive) c.material.emissive.setHex(0x000000);
        });
      }
      this.hoveredDoor = null;
    }
    if (this.hoveredWindow && this.hoveredWindow !== win) {
      if (!this.hoveredWindow.isSelected) {
        this.hoveredWindow.mesh?.traverse(c => {
          if (c.material?.emissive) c.material.emissive.setHex(0x000000);
        });
      }
      this.hoveredWindow = null;
    }
    if (this.hoveredFurniture && this.hoveredFurniture !== furn) {
      if (!this.hoveredFurniture.isSelected) {
        this.hoveredFurniture.mesh?.traverse(c => {
          if (c.isMesh && c.material) {
            const mats = Array.isArray(c.material) ? c.material : [c.material];
            mats.forEach(m => { if (m.emissive) m.emissive.setHex(0x000000); });
          }
        });
      }
      this.hoveredFurniture = null;
    }

    if (wall) {
      if (!this.selectedWalls.includes(wall)) {
        wall.mesh?.traverse(c => {
          if (c.isMesh && c.material?.emissive) c.material.emissive.setHex(0xddeeff);
        });
      }
      this.hoveredWall = wall;
    }
    if (door) {
      if (!door.isSelected) {
        door.mesh?.traverse(c => {
          if (c.material?.emissive) c.material.emissive.setHex(0xddeeff);
        });
      }
      this.hoveredDoor = door;
    }
    if (win) {
      if (!win.isSelected) {
        win.mesh?.traverse(c => {
          if (c.material?.emissive) c.material.emissive.setHex(0xddeeff);
        });
      }
      this.hoveredWindow = win;
    }
    if (furn) {
      if (!furn.isSelected) {
        furn.mesh?.traverse(c => {
          if (c.isMesh && c.material) {
            const mats = Array.isArray(c.material) ? c.material : [c.material];
            mats.forEach(m => { if (m.emissive) m.emissive.setHex(0xddeeff); });
          }
        });
      }
      this.hoveredFurniture = furn;
    }
    if (wall || door || win || furn ||
        this.hoveredWall !== wall ||
        this.hoveredDoor !== door ||
        this.hoveredWindow !== win ||
        this.hoveredFurniture !== furn) {
      window.requestRender?.();
    }
  }

  // RAYCASTING
  _setMouse(event) {
    const r = this.domElement.getBoundingClientRect();
    this.mouse.x =  ((event.clientX - r.left) / r.width)  * 2 - 1;
    this.mouse.y = -((event.clientY - r.top)  / r.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
  }

  // Versione che accetta coordinate client dirette (usata da touch handlers)
  _setMouseFromClient(clientX, clientY) {
    const r = this.domElement.getBoundingClientRect();
    this.mouse.x =  ((clientX - r.left) / r.width)  * 2 - 1;
    this.mouse.y = -((clientY - r.top)  / r.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
  }

  // TOUCH HANDLERS
  // Crea un event-like object da un touch, compatibile con i metodi mouse
  _touchToEvent(touch) {
    return { clientX: touch.clientX, clientY: touch.clientY, ctrlKey: false, metaKey: false };
  }

  _onTouchStart(event) {
    if (this.isLocked) {
      // Se un drag era in corso quando è scattato il lock, interrompilo
      this.isDragging     = false;
      this.isDraggingWall = false;
      this.isMultiDrag    = false;
      if (this.controls) this.controls.enabled = true;
      return;
    }

    // DOPPIO TAP — il secondo touchstart arriva entro 250ms dall'ultimo touchend
    // In questo caso non avviare drag né selezione
    if (Date.now() - this._lastTapEndTime < 250) {
      this.cancelFurnitureSelect();
      this.cancelDoorWindowSelect();
      return;
    }

    const touch = event.changedTouches[0];
    const synth = this._touchToEvent(touch);

    const wall = this.getIntersectedWall(synth);
    const door = this.getIntersectedDoor(synth);
    const win  = this.getIntersectedWindow(synth);
    const furn = this.getIntersectedFurniture(synth);

    // Se il drawing mode è attivo e l'utente tocca un oggetto esistente,
    // disattiva automaticamente il disegno pareti prima di procedere
    if (this.wallController?.drawingMode && (wall || door || win || furn)) {
      this.wallController.disableDrawMode();
      if (window.app) window.app.updateAllButtons();
    }

    // Avvia drag solo se ha colpito qualcosa (per non bloccare OrbitControls su touch libero)
    if (furn || wall || door || win) {      
      event.preventDefault();
      this._isTouchDrag = true;
      if (furn) {
        // Come per il mouse: cancella il timer del tap precedente per evitare
        // che attivi i bottoni prima che il doppio tap venga rilevato
        this.cancelFurnitureSelect();
        this.startDrag(synth, furn, false);  // no highlight: potrebbe essere un doppio tap
      } else if (wall) {
        this.startWallDrag(synth, wall, false);
      } else {
        this.cancelDoorWindowSelect();
      }
    }
  }

  _onTouchMove(event) {
    if (this.isLocked) return;
    if (!this.isMultiDrag && !this.isDragging && !this.isDraggingWall) return;
    event.preventDefault();
    const touch = event.changedTouches[0];
    const synth = this._touchToEvent(touch);
    if (this.isMultiDrag)          this.updateMultiDrag(synth);
    else if (this.isDragging)      this.updateDrag(synth);
    else if (this.isDraggingWall)  this.updateWallDrag(synth);
  }

  _onTouchEnd(event) {
    // Rilevamento doppio tap: calcola l'intervallo prima di aggiornare il timestamp
    const now = Date.now();
    const isDoubleTap = now - this._lastTapEndTime < 250;
    this._lastTapEndTime = now;

    // Il drag touch è terminato: d'ora in poi onSelectionChange può notificare normalmente
    this._isTouchDrag = false;

    const touch = event.changedTouches[0];
    const synth = this._touchToEvent(touch);

    if (this.isMultiDrag)          { this.endMultiDrag(synth); return; }
    if (this.isDragging)           { this.endDrag(synth);      return; }
    if (this.isDraggingWall)       { this.endWallDrag(synth);  return; }

    // Tap semplice → selezione (bloccata se progetto è in modalità lucchetto)
    if (this.isLocked) return;

    // DOPPIO TAP
    // Annulla qualsiasi selezione pendente e deseleziona tutto per non mostrare bottoni né highlight
    if (isDoubleTap) {
      this.cancelFurnitureSelect();
      this.cancelDoorWindowSelect();
      this.deselectAll();
      return;
    }

    const wall = this.getIntersectedWall(synth);
    const door = this.getIntersectedDoor(synth);
    const win  = this.getIntersectedWindow(synth);
    const furn = this.getIntersectedFurniture(synth);

    if (furn) {
      // Debounce: aspetta 250ms prima di applicare l'highlight,
      // così il doppio tap può cancellare il timer prima che scatti
      clearTimeout(this._furnitureSelectTimer);
      this._furnitureSelectTimer = setTimeout(() => {
        this.selectSingleFurniture(furn);
        this.onSelectionChange?.();
      }, 250);
      return;
    } else if (wall) {
      clearTimeout(this._doorWindowSelectTimer);
      this._doorWindowSelectTimer = setTimeout(() => {
        this.selectSingleWall(wall);
        this.onSelectionChange?.();
      }, 250);
      return;
    } else if (door) {
      clearTimeout(this._doorWindowSelectTimer);
      this._doorWindowSelectTimer = setTimeout(() => {
        this.selectSingleDoor(door);
        this.onSelectionChange?.();
      }, 250);
      return;
    } else if (win) {
      clearTimeout(this._doorWindowSelectTimer);
      this._doorWindowSelectTimer = setTimeout(() => {
        this.selectSingleWindow(win);
        this.onSelectionChange?.();
      }, 250);
      return;
    } else {
      this.deselectAll();
    }
    this.onSelectionChange?.();
  }

  getIntersectedWall(event) {
    if (!this.wallController) return null;
    this._setMouse(event);
    const meshes = [], map = new Map();
    this.wallController.getAllWalls().forEach(w => {
      w.mesh?.traverse(c => { if (c.isMesh) { meshes.push(c); map.set(c, w); } });
    });
    const hits = this.raycaster.intersectObjects(meshes, false);
    return hits.length ? (map.get(hits[0].object) ?? null) : null;
  }

  getIntersectedDoor(event) {
    if (!this.doorController) return null;
    this._setMouse(event);
    const meshes = [];
    this.doorController.getAllDoors().forEach(d => {
      d.mesh?.traverse(c => { if (c.isMesh) meshes.push(c); });
    });
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (!hits.length) return null;
    let o = hits[0].object;
    while (o && !o.userData.door) o = o.parent;
    return o?.userData.door ?? null;
  }

  getIntersectedWindow(event) {
    if (!this.windowController) return null;
    this._setMouse(event);
    const meshes = [];
    this.windowController.getAllWindows().forEach(w => {
      w.mesh?.traverse(c => { if (c.isMesh) meshes.push(c); });
    });
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (!hits.length) return null;
    let o = hits[0].object;
    while (o && !o.userData.window) o = o.parent;
    return o?.userData.window ?? null;
  }

  getIntersectedFurniture(event) {
    this._setMouse(event);
    const meshes = [];
    this.furnitureController.getAllFurniture().forEach(f => {
      f.mesh?.traverse(c => { if (c.isMesh) meshes.push(c); });
    });
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (!hits.length) return null;
    let o = hits[0].object;
    while (o && !o.userData.furniture) o = o.parent;
    return o?.userData.furniture ?? null;
  }

  getGroundIntersection(event) {
    this._setMouse(event);
    const pt = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(this.groundPlane, pt);
    return pt;
  }

  cancelDoorWindowSelect() {
    clearTimeout(this._doorWindowSelectTimer);
    this._doorWindowSelectTimer = null;
  }

  cancelFurnitureSelect() {
    clearTimeout(this._furnitureSelectTimer);
    this._furnitureSelectTimer = null;
  }

  getSelectedDoors()     { return this.selectedDoors;     }
  getSelectedWindows()   { return this.selectedWindows;   }
  getSelectedFurniture() { return this.selectedFurniture; }

  // DISPOSE
  dispose() {
    this._hideSnapMarkers();
    if (this._snapSceneAdded && this.furnitureController?.scene) {
      const s = this.furnitureController.scene;
      this._snapMarkers.forEach(m => {
        s.remove(m);
        m.geometry.dispose();
        m.material.dispose();
      });
    }
    // Reset hover emissive
    [this.hoveredFurniture, this.hoveredDoor, this.hoveredWindow].forEach(obj => {
      if (obj && !obj.isSelected) {
        obj.mesh?.traverse(c => {
          if (c.isMesh && c.material) {
            const mats = Array.isArray(c.material) ? c.material : [c.material];
            mats.forEach(m => { if (m.emissive) m.emissive.setHex(0x000000); });
          }
        });
      }
    });
    this.domElement.removeEventListener('mousedown', this.onMouseDown);
    this.domElement.removeEventListener('mousemove', this.onMouseMove);
    this.domElement.removeEventListener('mouseup',   this.onMouseUp);
    this.domElement.removeEventListener('click',     this.onClick);
    document.removeEventListener('keydown', this.onKeyDown);
    this.domElement.removeEventListener('touchstart', this._onTouchStart);
    this.domElement.removeEventListener('touchmove',  this._onTouchMove);
    this.domElement.removeEventListener('touchend',   this._onTouchEnd);
  }
}

export default SelectionController;